/* eslint-disable array-callback-return */
import axios from "axios";
import store from "store";
import { deleteField, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

// Constants
import { PLATFORM_SERVICES_COLLECTION } from "constants/global/firebase-collections";
import { GOOGLE_ADS_SCOPES } from "constants/googleAds";

// Functions
import {
  FIREBASE_EVENTS,
  addPlatformService,
  sweetAlert,
  updatePlatformServices,
} from "./global";
import { ApiServerPath } from "api_server";
import { updateYouTubeChannelInfo } from "./youtube";

export const getGoogleAccountIssues = async (
  customerid: string,
  managerId: string
) => {
  try {
    const { platformServices = {}, userData = {} }: any =
      store.getState()?.appReducer;
    const { lastSelectedWorkspace = {} } = userData;
    const { workspaceId } = lastSelectedWorkspace;
    const { googleAdwords = {} } = platformServices;

    const { refreshToken } = googleAdwords;

    const { data } = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_GOOGLE_ACCOUNT_ISSUES}`,
      {
        refreshToken,
        managerId,
        customerid,
        workspaceId,
      }
    );

    if (data?.results?.message) {
      if (
        data?.results?.message ===
        "Your billling setup is not approved in Google Ads. Please contact your Google Ads manager to approve your billing setup."
      ) {
        return `Your billing information is not up-to-date in your Google Ads Manager. Please follow these instructions to add or update your billing information:\n https://support.google.com/youtube/answer/2375375?hl=en`;
      } else {
        return data?.results?.message;
      }
    } else {
      return false;
    }
  } catch (error) {
    console.error(error, "error");
  }
};

export const getGoogleAdsAccounts = async (refreshToken: any) => {
  try {
    const { data } = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_API_SERVER_ADWORDS_AD_ACCOUNTS}  `,
      { refreshToken }
    );

    if (data?.results?.accounts) {
      const { accounts } = data?.results;
      for (let index = 0; index < accounts.length; index++) {
        const element = accounts[index];
        if (element?.childAccounts?.length) {
          element.childAccounts.map((item: any) => {
            item.managerId = element?.customerId;
          });
        }
        element.name = element?.name || element?.customerId;
      }
      return accounts;
    }
    return [];
  } catch (error) {
    console.log("getAccounts error in adwords *** ", error);
  }
};

export const getRefreshToken = async (serverAuthCode: any, platform?: any) => {
  const hostname = window.location.hostname;

  const { userData = {} }: any = store.getState().appReducer;
  const { lastSelectedWorkspace = {} } = userData;
  const { workspaceId } = lastSelectedWorkspace;

  const data = {
    code: serverAuthCode,
    redirectUri:
      hostname === "localhost"
        ? "http://localhost:3000"
        : hostname === "easyconnectme.com"
        ? "https://easyconnectme.com"
        : "https://app.advertgenerator.com",
  };
  try {
    const responseForRefreshToken: any = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_GOOGLE_REFRESH_TOKEN}`,
      data
    );

    console.log(responseForRefreshToken, "responseForRefreshToken");

    const refreshToken =
      responseForRefreshToken?.data?.results?.auth?.refresh_token;

    if (platform === "googleAdwords" || platform === "googleSocial") {
      const ref = doc(PLATFORM_SERVICES_COLLECTION, workspaceId);
      const getData = await getDoc(ref);
      const data = getData.data();
      const userName = responseForRefreshToken?.data?.results?.user?.name;
      const email = responseForRefreshToken?.data?.results?.user?.email;
      if (getData.exists()) {
        if (data?.[platform]) {
          updatePlatformServices(`${platform}.userName`, userName);
          updatePlatformServices(`${platform}.email`, email);
        } else {
          updateDoc(ref, {
            [platform]: {
              userName,
              email,
            },
          });
        }
      } else {
        setDoc(ref, {
          [platform]: {
            userName,
            email,
          },
        });
      }
    }

    return {refreshToken, auth: responseForRefreshToken?.data?.results?.auth};
  } catch (err) {
    console.log(err);
    throw new Error((err as any).message);
  }
};

export const updatePlatforms = async (platform: any, result: any) => {
  try {
    const { code } = result;
    const refreshToken = await getRefreshToken(code, platform);

    const data = refreshToken?.auth;
    let isScope;
    if (data?.scope) {
      if (platform === "googleAdwords") {
        isScope = data.scope.indexOf(GOOGLE_ADS_SCOPES) !== -1;
      } else if (platform === "googleSocial") {
        isScope =
          data.scope.indexOf("https://www.googleapis.com/auth/youtube.upload") !==
            -1 ||
          data.scope.indexOf(
            "https://www.googleapis.com/auth/youtube.force-ssl",
          ) !== -1 ||
          data.scope.indexOf(
            "https://www.googleapis.com/auth/youtube.readonly",
          ) !== -1;
      } else if (platform === "googleAnalytics") {
        isScope =
          data.scope.indexOf(
            "https://www.googleapis.com/auth/analytics.readonly"
          ) !== -1 &&
          data.scope.indexOf(
            "https://www.googleapis.com/auth/analytics.manage.users"
          ) !== -1;
      } else {
        isScope =
          data.scope.indexOf(
            "https://www.googleapis.com/auth/youtube.readonly"
          ) !== -1 &&
          data.scope.indexOf(
            "https://www.googleapis.com/auth/yt-analytics-monetary.readonly"
          ) !== -1;
      }
    }

    if (isScope) {
      const { userData = {} }: any = store.getState().appReducer;
      const { lastSelectedWorkspace = {} } = userData;
      const { workspaceId } = lastSelectedWorkspace;
      await addPlatformService(platform, refreshToken?.refreshToken, workspaceId);

      // YouTube social connection only — keep Ads and Social separate.
      // Channel lookup + lock check run after tokens are saved; on failure
      // updateYouTubeChannelInfo removes the incomplete googleSocial entry.
      if (platform === "googleSocial") {
        if (!data?.access_token) {
          try {
            await updateDoc(doc(PLATFORM_SERVICES_COLLECTION, workspaceId), {
              googleSocial: deleteField(),
            });
          } catch (cleanupError) {
            console.log(
              "Google Social missing-token cleanup failed:",
              cleanupError,
            );
          }
          sweetAlert(
            "Failed to verify YouTube channel for this Google account. Please try connecting again.",
            "error",
          );
          return;
        }

        const channelConnected = await updateYouTubeChannelInfo(
          data.access_token,
          workspaceId,
          "googleSocial",
        );
        if (!channelConnected) {
          return;
        }
      }

      const screenName =
        platform === "googleAnalytics"
          ? "Google Analytics Analytics"
          : platform === "googleSocial"
          ? "Google Social / YouTube"
          : platform === "googleAdwords"
          ? "Google Ads Analytics"
          : "Youtube Analytics";

      FIREBASE_EVENTS("view_content", screenName);
    } else {
      setTimeout(() => {
        sweetAlert(
          `You must allow access to ${
            platform === "googleAdwords"
              ? "Google Ads"
              : platform === "googleAnalytics"
              ? "Google Analytics"
              : "Youtube Analytics"
          } permissions to continue.`
        );
      }, 1500);
    }
  } catch (error) {
    sweetAlert(error, "error", null);
  }
};
